import apiClient from "../axios";

/**
 * Blocks Storage (DMS) cleanup.
 *
 * Deleting a post / news article / event / wiki page removed the record but left
 * every uploaded attachment sitting in storage forever. These helpers delete the
 * files too.
 *
 * Contract verified live 2026-07-30 against the project:
 *   POST /data/v4/Files/GetPreSignedUrlForUpload → {uploadUrl, fileId, isSuccess}
 *   POST /data/v4/Files/DeleteFile  {fileId, projectKey} → 200 {isSuccess: true}
 *   GET  /data/v4/Files/GetFile?FileId=…           → 204 once deleted
 */

const PROJECT_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY;

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Recover the `fileId` from a stored attachment URL.
 *
 * The app stores the bare upload URL, not the `fileId`, so it has to be parsed
 * back out. A presigned upload URL looks like:
 *
 *   https://blocksprod.blob.core.windows.net
 *     /<tenant key>/Public/<fileId>/<upload id>/<file name>
 *
 * The tenant key is undashed hex so it cannot match, and the file name is not a
 * UUID — the **first** dashed UUID in the path is the `fileId` (confirmed against
 * the `fileId` the presign call returned for that same URL).
 */
export const extractFileId = (url: string): string | null => {
  if (!url) return null;
  try {
    const { pathname } = new URL(url);
    const match = pathname.match(UUID);
    return match?.[0] ?? null;
  } catch {
    // Not an absolute URL — fall back to scanning the raw string.
    return url.match(UUID)?.[0] ?? null;
  }
};

/** True for strings that look like a stored file rather than any other URL. */
const isStoredFileUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value) && !!extractFileId(value);

/**
 * Collect every stored-file URL reachable from an entity, at any depth.
 *
 * Deliberately a recursive walk rather than a per-entity field list: the four
 * entity types spell their attachments differently — posts use a flat
 * `attachmentUrls: [String]`, news and wiki use a nested `attachmentUrls { … }`
 * object plus `coverImageUrl`, and events use `attachmentUrls` plus `coverImage`.
 * A walk covers all of them and keeps covering them when a new field is added.
 */
export const collectFileUrls = (entity: unknown): string[] => {
  const found = new Set<string>();

  const visit = (node: unknown, depth: number) => {
    if (node == null || depth > 6) return;
    if (typeof node === "string") {
      if (isStoredFileUrl(node)) found.add(node);
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((child) => visit(child, depth + 1));
      return;
    }
    if (typeof node === "object") {
      Object.values(node as Record<string, unknown>).forEach((child) =>
        visit(child, depth + 1),
      );
    }
  };

  visit(entity, 0);
  return [...found];
};

/** Delete one file by id. Resolves `false` instead of throwing. */
export const deleteStorageFile = async (fileId: string): Promise<boolean> => {
  try {
    const res = await apiClient.post("/data/v4/Files/DeleteFile", {
      fileId,
      projectKey: PROJECT_KEY,
    });
    return res.data?.isSuccess !== false;
  } catch {
    return false;
  }
};

/**
 * Best-effort cleanup of everything an entity had attached.
 *
 * Never throws and never rejects: the record is already gone by the time this
 * runs, so a storage failure must not surface as "delete failed" — that would be
 * strictly worse than an orphaned blob. Failures are counted and logged.
 */
export const deleteEntityFiles = async (entity: unknown): Promise<void> => {
  const urls = collectFileUrls(entity);
  if (!urls.length) return;

  const ids = [...new Set(urls.map(extractFileId).filter(Boolean))] as string[];
  const results = await Promise.all(ids.map(deleteStorageFile));

  const failed = results.filter((ok) => !ok).length;
  if (failed) {
    console.warn(
      `Storage cleanup: ${failed}/${ids.length} attachment(s) could not be deleted`,
    );
  }
};
